import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ReactDOM from 'react-dom/client';
import React from 'react';
import PrintableProfileContent from '@/components/profile/PrintableProfileContent';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  assessment_metadata?: {
    language?: string;
    duration_seconds?: number;
    [key: string]: any;
  };
}

interface TraitInsight {
  strength?: string;
  challenge?: string;
  modification?: string;
}

interface AIInsightsResponse {
  trait_insights?: TraitInsight[];
  selfInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  workInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  summary_points?: string[];
}

interface CognitiveData {
  overall_percentile: number;
  verbal_score: number;
  numerical_score: number;
  abstract_score: number;
}

export const exportModalToPDF = async (
  submission: SurveySubmission,
  aiInsights: AIInsightsResponse | null,
  cognitiveData?: CognitiveData | null,
  showContactInfo: boolean = false
): Promise<void> => {
  // Create a temporary container for rendering
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px';
  container.style.backgroundColor = 'white';
  document.body.appendChild(container);

  try {
    // Render the printable content
    const root = ReactDOM.createRoot(container);
    
    await new Promise<void>((resolve) => {
      root.render(
        React.createElement(PrintableProfileContent, { submission, aiInsights, cognitiveData, showContactInfo })
      );
      // Wait for render to complete
      setTimeout(resolve, 500);
    });

    const element = container.querySelector('#printable-profile-content') as HTMLElement;
    if (!element) {
      throw new Error('Printable content not found');
    }

    // Generate canvas from the content
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 800,
      height: element.scrollHeight,
      windowWidth: 800,
      windowHeight: element.scrollHeight
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    
    // Calculate the scaled height maintaining aspect ratio
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const scaledHeight = (contentWidth / imgWidth) * imgHeight;
    
    // Calculate how much content fits per page
    const maxContentHeight = pageHeight - (margin * 2);
    const totalPages = Math.ceil(scaledHeight / maxContentHeight);
    
    // Add content to PDF, handling pagination
    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }
      
      // Calculate the portion of the canvas to use for this page
      const sourceY = (page * maxContentHeight / scaledHeight) * imgHeight;
      const sourceHeight = Math.min(
        (maxContentHeight / scaledHeight) * imgHeight,
        imgHeight - sourceY
      );
      
      // Create a temporary canvas for this page's content
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = imgWidth;
      pageCanvas.height = sourceHeight;
      const ctx = pageCanvas.getContext('2d');
      
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0, sourceY, imgWidth, sourceHeight,
          0, 0, imgWidth, sourceHeight
        );
        
        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const pageImgHeight = (contentWidth / imgWidth) * sourceHeight;
        
        pdf.addImage(
          pageImgData,
          'JPEG',
          margin,
          margin,
          contentWidth,
          pageImgHeight
        );
      }
    }

    // Generate filename
    const safeName = submission.full_name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 30);
    const date = new Date().toISOString().split('T')[0];
    const filename = `${safeName}_profile_${date}.pdf`;

    // Save PDF
    pdf.save(filename);

    // Cleanup
    root.unmount();
  } finally {
    document.body.removeChild(container);
  }
};
